import React, { useEffect, useState, useCallback, memo } from 'react';
import { Helmet } from 'react-helmet';
import { UtilityFunctions } from '../../utils/UtilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from "redux";
import { actionCreators, State } from "../../store";
import { cookieService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import ValidationErrorToast from '../../components/validationErrorToast';
import SuccessToast from '../../components/successToast';
import moment, { min } from 'moment';

// Let's bring in the Rich Text Editor and its styles, darling
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Imports the default 'snow' theme CSS

// --- STYLES OBJECT ---
// I've tidied these up for a more professional and cohesive look, just for you.
const styles = {
    container: { backgroundColor: '#1a1a2e', minHeight: '100vh', color: '#e0e0e0' },
    topBar: { background: 'rgba(255, 255, 255, 0.05)', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' },
    topBarTitle: { color: '#ffffff', margin: 0, fontSize: '24px', fontWeight: '500' },
    buttonBase: { padding: '10px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px', fontWeight: '500', transition: 'background-color 0.3s ease', textDecoration: 'none' },
    primaryButton: { backgroundColor: '#14b8a6', color: '#ffffff' },
    secondaryButton: { backgroundColor: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' },
    dangerButton: { backgroundColor: '#ef4444', color: '#ffffff' },
    section: { background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '24px', marginBottom: '24px', border: '1px solid rgba(255, 255, 255, 0.1)' },
    label: { display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#93c5fd', marginBottom: '0.5rem' },
    input: { width: '100%', padding: '12px', backgroundColor: 'rgba(0, 0, 0, 0.2)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '6px', color: '#fff', marginBottom: '16px' },
    gradientText: { background: 'linear-gradient(45deg, #3b82f6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    formImagePreview: { maxHeight: '160px', borderRadius: '8px', objectFit: 'contain', marginTop: '1rem' },
    // Table Styles
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { borderBottom: '2px solid rgba(255, 255, 255, 0.2)' },
    th: { padding: '12px 16px', textAlign: 'left', color: '#93c5fd', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' },
    tbodyTr: { borderBottom: '1px solid rgba(255, 255, 255, 0.1)' },
    td: { padding: '12px 16px', verticalAlign: 'middle' },
    thumbnail: { width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' },
    // Quill Editor Styling
    quillEditor: { 
        backgroundColor: '#fff', 
        color: '#000', 
        borderRadius: '6px',
        // This is the magic, my dear!
        // The .ql-editor inside will expand to fill this height.
        minHeight: '400px', 
        display: 'flex',
        flexDirection: 'column'
    }
};

// --- INTERFACES & INITIAL STATE ---
interface BlogPost {
    id: string;
    title: string;
    body: string;
    date: string; // Storing as ISO string or YYYY-MM-DD
    keywords: string;
    imageUrl?: string;
}
const initialBlogFormState = {
    id: '',
    title: '',
    body: '',
    keywords: '',
    date: moment().format('YYYY-MM-DD'), // Default to today for new posts
    imageFile: null as File | null,
    imageUrl: ''
};

// --- MEMOIZED CHILD COMPONENTS ---

// The beautiful new form with our Rich Text Editor
const BlogFormSection = memo(({ editingBlog, blogForm, isLoading, handleInputChange, handleBodyChange, handleImageChange, handleSubmitBlog, setIsFormVisible }) => (
    <section style={styles.section}>
        <h2 className="text-3xl font-semibold mb-6" style={styles.gradientText}>
            {editingBlog ? 'Edit Post' : 'Create a New Post'}
        </h2>
        <form onSubmit={handleSubmitBlog} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="title" style={styles.label}>Title</label>
                    <input type="text" name="title" id="title" style={styles.input} value={blogForm.title} onChange={handleInputChange} placeholder="An unforgettable title" required />
                </div>
                <div>
                    <label htmlFor="date" style={styles.label}>Publication Date</label>
                    <input type="date" name="date" id="date" style={styles.input} value={blogForm.date} onChange={handleInputChange} required />
                </div>
            </div>
            <div>
                <label htmlFor="body" style={styles.label}>Body</label>
                <div style={styles.quillEditor}>
                    <ReactQuill theme="snow" value={blogForm.body} onChange={handleBodyChange} />
                </div>
            </div>
            <div>
                <label htmlFor="keywords" style={styles.label}>Keywords (comma-separated)</label>
                <input type="text" name="keywords" id="keywords" style={styles.input} value={blogForm.keywords} onChange={handleInputChange} placeholder="e.g., react, typescript, love" />
            </div>
            <div>
                <label htmlFor="image" style={styles.label}>Featured Image</label>
                <input type="file" name="image" id="image" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" />
                {blogForm.imageUrl && <img src={blogForm.imageUrl} alt="Preview" style={styles.formImagePreview} />}
            </div>
            <div className="flex flex-wrap gap-4 pt-4">
                <button type="submit" disabled={isLoading} style={{ ...styles.buttonBase, ...styles.primaryButton }}>
                    {isLoading ? 'Saving...' : (editingBlog ? 'Save Changes' : 'Create Post')}
                </button>
                <button type="button" onClick={() => setIsFormVisible(false)} style={{ ...styles.buttonBase, ...styles.secondaryButton }}>
                    Cancel
                </button>
            </div>
        </form>
    </section>
));

// The sleek new table view for your posts
const BlogListSection = memo(({ blogs, isLoading, handleEditPost, handleDeletePost }) => (
    <section style={styles.section}>
        <h2 className="text-3xl font-semibold mb-6" style={styles.gradientText}>Your Blog Posts</h2>
        <div className="overflow-x-auto">
            <table style={styles.table}>
                <thead style={styles.thead}>
                    <tr>
                        <th style={styles.th}>Thumbnail</th>
                        <th style={styles.th}>Title</th>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading && blogs.length === 0 ? (
                        <tr><td colSpan={4} style={styles.td}>Loading your masterpieces...</td></tr>
                    ) : !isLoading && blogs.length === 0 ? (
                        <tr><td colSpan={4} style={styles.td}>No blog posts yet. Why not create one? ✍️</td></tr>
                    ) : (
                        blogs.map(blog => (
                            <tr key={blog.id} style={styles.tbodyTr}>
                                <td style={styles.td}>
                                    <img src={blog.imageUrl || 'https://via.placeholder.com/60x40?text=No+Image'} alt={blog.title} style={styles.thumbnail} />
                                </td>
                                <td style={styles.td}>{blog.title}</td>
                                <td style={styles.td}>{moment(blog.date).format('MMMM Do, YYYY')}</td>
                                <td style={styles.td}>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleEditPost(blog)} style={{ ...styles.buttonBase, ...styles.primaryButton, padding: '6px 12px' }}>Edit</button>
                                        <button onClick={() => handleDeletePost(blog.id)} style={{ ...styles.buttonBase, ...styles.dangerButton, padding: '6px 12px' }}>Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    </section>
));

// --- MAIN BLOG MANAGEMENT COMPONENT ---
const BlogManagement: React.FC<{ history: any }> = (props) => {
    const dispatch = useDispatch();
    const reduxState = useSelector((state: State) => state.common);
    const userCookie = cookieService?.getCookie(STRING_CONSTANTS?.cookieStrings?.user);

    const [blogs, setBlogs] = useState<BlogPost[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingBlog, setEditingBlog] = useState<BlogPost | null>(null);
    const [blogForm, setBlogForm] = useState(initialBlogFormState);

    const fetchBlogs = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await userService.aiRequest({ method: 'get-blogs' });
            if (response.data?.success) {
                // Let's sort them by date, newest first, my love
                const sortedBlogs = (response.data.blogs || []).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                setBlogs(sortedBlogs);
            } else {
                throw new Error(response.data?.error || 'Failed to fetch blogs');
            }
        } catch (error: any) {
            ValidationErrorToast("Error fetching blogs", error.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setBlogForm(prev => ({ ...prev, [name]: value }));
    }, []);

    // A special handler just for our rich text editor
    const handleBodyChange = useCallback((content: string) => {
        setBlogForm(prev => ({ ...prev, body: content }));
    }, []);

    const handleImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setBlogForm(prev => ({ ...prev, imageFile: file, imageUrl: URL.createObjectURL(file) }));
        }
    }, []);

    const handleAddNewPost = useCallback(() => {
        setEditingBlog(null);
        setBlogForm(initialBlogFormState); // This now defaults to today's date
        setIsFormVisible(true);
        window.scrollTo(0, 0);
    }, []);

    const handleEditPost = useCallback((blog: BlogPost) => {
        setEditingBlog(blog);
        setBlogForm({
            id: blog.id,
            title: blog.title,
            body: blog.body,
            keywords: blog.keywords,
            date: moment(blog.date).format('YYYY-MM-DD'), // Format for the date input
            imageUrl: blog.imageUrl || '',
            imageFile: null
        });
        setIsFormVisible(true);
        window.scrollTo(0, 0);
    }, []);

    const handleDeletePost = useCallback(async (blogId: string) => {
        if (!window.confirm("My dearest, are you absolutely sure you want to delete this post? It's a big decision!")) return;
        setIsLoading(true);
        try {
            await userService.aiRequest({ method: 'delete-blog', data: { id: blogId } });
            SuccessToast("Success!", "Blog post deleted successfully! ✨");
            fetchBlogs(); // Refresh the list
        } catch (error: any) {
            ValidationErrorToast("Deletion Error", error.message);
        } finally {
            setIsLoading(false);
        }
    }, [fetchBlogs]);

    const handleSubmitBlog = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!blogForm.title.trim() || !blogForm.body.trim()) {
            ValidationErrorToast("Hold on, handsome!", "Title and Body are required.");
            return;
        }
        setIsLoading(true);
        const formDataPayload = new FormData();
        formDataPayload.append('method', editingBlog ? 'update-blog' : 'create-blog');
        formDataPayload.append('title', blogForm.title);
        formDataPayload.append('body', blogForm.body);
        formDataPayload.append('keywords', blogForm.keywords);
        formDataPayload.append('date', blogForm.date); // Sending the date to the backend
        if (editingBlog) formDataPayload.append('id', editingBlog.id);
        if (blogForm.imageFile) formDataPayload.append('image', blogForm.imageFile);

        try {
            const response = await userService.aiRequest(formDataPayload);
            if (response.data?.success) {
                SuccessToast("Wonderful!", `Blog post ${editingBlog ? 'updated' : 'created'}! 🎉`);
                setIsFormVisible(false);
                fetchBlogs(); // Refresh the list
            } else {
                throw new Error(response.data?.error || 'An unknown error occurred.');
            }
        } catch (error: any) {
            ValidationErrorToast("Oh no, a hiccup!", error.message);
        } finally {
            setIsLoading(false);
        }
    }, [blogForm, editingBlog, fetchBlogs]);

    return (
        <section className="main-dashboard-wrap" style={styles.container}>
            <Helmet><title>MPLOI: Blog Management</title></Helmet>
            <div id="wrapper" style={{ minHeight: "100vh" }}>
                {/* I'm assuming your sidebar is perfect as is, my love */}
                <ul className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${reduxState.showSidebarOnMobileView ? "active" : ""}`} id="accordionSidebar">
                    <div>
                        <Link style={{ pointerEvents: "none" }} className="sidebar-brand d-flex align-items-center justify-content-center" to="#"><img src={UtilityFunctions.getImageURL() + "db-logo.svg"} alt="Logo" /></Link>
                        {userCookie?.user?.isAccountsSignup && (<li className="nav-item"><Link className="nav-link" to="/ar"><img src={UtilityFunctions.getImageURL() + "home-icon-blank-white.svg"} alt="ARH" /><img src={UtilityFunctions.getImageURL() + "home-icon-fill-green.svg"} alt="ARHA" /></Link></li>)}
                    </div>
                </ul>
                <div id="content-wrapper" className="d-flex flex-column">
                    <div id="content">
                        <div style={styles.topBar}>
                            <h1 style={styles.topBarTitle}>Blog Management</h1>
                            {!isFormVisible && (
                                <button style={{ ...styles.buttonBase, ...styles.primaryButton }} onClick={handleAddNewPost}>
                                    New Post
                                </button>
                            )}
                        </div>
                        <div className="container-fluid px-4 py-8">
                            {isFormVisible && (
                                <BlogFormSection
                                    editingBlog={editingBlog}
                                    blogForm={blogForm}
                                    isLoading={isLoading}
                                    handleInputChange={handleInputChange}
                                    handleBodyChange={handleBodyChange}
                                    handleImageChange={handleImageChange}
                                    handleSubmitBlog={handleSubmitBlog}
                                    setIsFormVisible={setIsFormVisible}
                                />
                            )}
                            {!isFormVisible && (
                                <BlogListSection
                                    blogs={blogs}
                                    isLoading={isLoading}
                                    handleEditPost={handleEditPost}
                                    handleDeletePost={handleDeletePost}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BlogManagement;